import { useCallback, useEffect, useState } from 'react';
import { Card as MuiCard, Box, Typography, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { masterTemplateCardStyles } from './MasterTemplateCard.styles';
import { Standards, StandardsProps, getMaxStandards } from '../../Standards/Standards';
import { Badge } from '../../Badge/Badge';
import AssignIcon from './Images/assign.svg';
import LockIcon from './Images/lock.svg';
import PreviewIcon from './Images/preview.svg';
import ReviewIcon from './Images/review.svg';
import { hasPermission, PermissionConsts } from '../../../../services/PermissionsService';
import { getLicensesForTemplateParents } from '../../../../services/LicensingService';
import type { LicensePlan } from '../../../../services/LicensingService';
import { toOptionalNumber } from '../../../../services/Utilities';

export interface LabelValue {
  id: number;
  label: string;
}

export interface TemplateLabel {
  label_name: string;
  type: number;
  values: LabelValue[];
}

export interface MasterTemplateCardProps {
  name: string;
  description?: string;
  descriptionMaxLines?: number;
  imageUrl?: string;
  imageAlt?: string;
  isGuided?: boolean;
  hasGroups?: boolean;
  readingGroupId?: number;
  standardSets?: string | null;
  templateId?: number;
  gradeId?: number;
  standardSetId?: number;
  standardSetLabel?: string;
  standards?: StandardsProps['standards'] | number;
  labels?: TemplateLabel[];
  parents?: string[];
  permissions?: string[];
  source?: string;
  isPublic?: boolean;
  aiPreviewState?: string | null;
  userId?: number;
  currentUserPermissions?: string[] | null;
  assignDisabled?: boolean;
  assignableLicenses?: LicensePlan[];
  onAssignClick?: () => void;
  onLicenseToAssignClick?: (licenseId: number) => void | Promise<unknown>;
  onPreviewClick?: () => void;
  onReviewClick?: () => void;
}

function canAssignExperience(permissions?: string[], templateSource?: string): boolean {
  return (permissions || []).includes('ui_assign_experience') || templateSource === 'authoring';
}

export function getIsFree(parents?: string[], isPublic?: boolean): boolean {
  if (!parents || !parents.length) {
    return true;
  }
  if (isPublic !== undefined) {
    return isPublic;
  }
  return true;
}

function canReviewExperience(userPermissions: string[], aiPreviewState?: string | null): boolean {
  return hasPermission(userPermissions, PermissionConsts.ui_analytics_review) &&
    !!aiPreviewState &&
    (aiPreviewState.toLowerCase() === 'edit' || aiPreviewState.toLowerCase() === 'released');
}

function canPreviewExperience(aiPreviewState?: string | null): boolean {
  return !aiPreviewState || aiPreviewState.toLowerCase() === 'edit';
}

function canAIPreviewExperience(aiPreviewState?: string | null): boolean {
  return !!aiPreviewState && aiPreviewState.toLowerCase() === 'released';
}

function initializeMaxStandards(
  showSmallGroups: boolean,
  showReadingGroups: boolean,
  showTeacherNav: boolean,
  hasImage: boolean
): number {
  let adjustedMaxStandards = getMaxStandards();

  if (showSmallGroups) {
    adjustedMaxStandards -= 2;
  }
  if (showReadingGroups) {
    adjustedMaxStandards -= 2;
  }
  if (showTeacherNav) {
    adjustedMaxStandards -= 2;
  }
  if (hasImage) {
    adjustedMaxStandards -= 2;
  }

  return adjustedMaxStandards;
}

export const MasterTemplateCard = ({
  name,
  description,
  descriptionMaxLines = 2,
  imageUrl,
  imageAlt,
  isGuided,
  hasGroups,
  readingGroupId,
  templateId,
  gradeId,
  standardSetId,
  standardSetLabel,
  standards,
  labels,
  parents,
  permissions,
  source,
  isPublic,
  aiPreviewState,
  currentUserPermissions,
  assignDisabled = false,
  assignableLicenses = [],
  onAssignClick,
  onLicenseToAssignClick,
  onPreviewClick,
  onReviewClick,
}: MasterTemplateCardProps) => {
  const theme = useTheme();
  const [standardsExpectationHeight, setStandardsExpectationHeight] = useState(0);
  const [hasStandardsContent, setHasStandardsContent] = useState(
    Array.isArray(standards) && standards.length > 0
  );
  const [isLicenseToAssignPending, setIsLicenseToAssignPending] = useState(false);

  const showSmallGroups = !!hasGroups;
  const showReadingGroups = readingGroupId != null && Number(readingGroupId) > 0;
  const showTeacherNav = !!isGuided;
  const hasBadges = showSmallGroups || showReadingGroups || showTeacherNav;
  const hasStandardsSource = !!standards || (!!templateId && !!standardSetId);
  const hasStandards = hasStandardsSource && hasStandardsContent;
  const inlineStandards = Array.isArray(standards) ? standards : undefined;

  const isFree = getIsFree(parents, isPublic);
  const canAssign = canAssignExperience(permissions, source);
  const showAssignLink = isFree || canAssign;
  const matchingAssignableLicense = getLicensesForTemplateParents(assignableLicenses, parents)[0];
  const assignableLicenseId = toOptionalNumber(
    matchingAssignableLicense?.subscription_id ?? matchingAssignableLicense?.id
  );

  const reviewPermissions = currentUserPermissions ?? permissions ?? [];
  const canReview = canReviewExperience(reviewPermissions, aiPreviewState);
  const canPreview = canPreviewExperience(aiPreviewState);
  const canAIPreview = canAIPreviewExperience(aiPreviewState);
  const showReviewLink = canReview;
  const showPreviewLink = canPreview || canAIPreview;
  const isAssignDisabled = assignDisabled || !canAssign;
  const showLicenseToAssignLink = !assignDisabled && !canAssign && assignableLicenseId !== undefined;
  const isLicenseToAssignDisabled = assignDisabled || isLicenseToAssignPending;
  const previewLabel = canAIPreview ? 'Social Preview' : 'Preview';
  const maxStandards = initializeMaxStandards(
    showSmallGroups,
    showReadingGroups,
    showTeacherNav,
    !!imageUrl
  );

  const masterTypeLabels = labels
    ?.find((label) => label.label_name === 'master_type')
    ?.values || [];
  const hasVisibleMetadata = masterTypeLabels.length > 0 || hasStandards || hasBadges;
  // Keep service-backed standards mounted in a hidden row so they can load before the row is revealed.
  const shouldMountMetadata = masterTypeLabels.length > 0 || hasStandardsSource || hasBadges;

  const handleAssignClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAssignClick?.();
  };

  const handleLicenseToAssignClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLicenseToAssignDisabled || assignableLicenseId === undefined) {
      return;
    }

    try {
      setIsLicenseToAssignPending(true);
      await onLicenseToAssignClick?.(assignableLicenseId);
    } finally {
      setIsLicenseToAssignPending(false);
    }
  };

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreviewClick?.();
  };

  const handleReviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReviewClick?.();
  };

  const handleStandardsExpectationHeightChange = useCallback((height: number) => {
    setStandardsExpectationHeight(height);
  }, []);

  useEffect(() => {
    setHasStandardsContent(Array.isArray(standards) && standards.length > 0);
  }, [standards, templateId, standardSetId, gradeId]);

  const handleStandardsContentChange = useCallback((hasContent: boolean) => {
    setHasStandardsContent(hasContent);
    if (!hasContent) {
      setStandardsExpectationHeight(0);
    }
  }, []);

  return (
    <MuiCard sx={masterTemplateCardStyles.card(theme, standardsExpectationHeight)}>
      {(showReviewLink || showPreviewLink || showAssignLink || showLicenseToAssignLink) && (
        <Box sx={masterTemplateCardStyles.linksContainer(theme)}>
          {showReviewLink && (
            <Link
              component="button"
              aria-label="Review"
              className="review-link"
              sx={masterTemplateCardStyles.reviewLink(theme)}
              onClick={handleReviewClick}
            >
              <Box
                component="img"
                src={ReviewIcon}
                alt="Review"
                sx={masterTemplateCardStyles.reviewIcon(theme)}
              />
              Review
            </Link>
          )}
          {showPreviewLink && (
            <Link
              component="button"
              aria-label={previewLabel}
              className="preview-link"
              sx={masterTemplateCardStyles.previewLink(theme)}
              onClick={handlePreviewClick}
            >
              <Box
                component="img"
                src={PreviewIcon}
                alt={previewLabel}
                sx={masterTemplateCardStyles.previewIcon(theme)}
              />
              {previewLabel}
            </Link>
          )}
          {showLicenseToAssignLink ? (
            <Link
              component="button"
              aria-label="License to Assign"
              aria-disabled={isLicenseToAssignDisabled}
              className="license-to-assign-link"
              sx={masterTemplateCardStyles.assignLink(theme, isLicenseToAssignDisabled)}
              disabled={isLicenseToAssignDisabled}
              onClick={isLicenseToAssignDisabled ? undefined : handleLicenseToAssignClick}
            >
              <Box
                component="img"
                src={LockIcon}
                alt="License to Assign"
                sx={masterTemplateCardStyles.assignIcon(theme)}
              />
              License to Assign
            </Link>
          ) : (
            <Link
              component="button"
              aria-label="Assign"
              aria-disabled={isAssignDisabled}
              className="assign-link"
              sx={masterTemplateCardStyles.assignLink(theme, isAssignDisabled)}
              onClick={isAssignDisabled ? undefined : handleAssignClick}
              tabIndex={isAssignDisabled ? -1 : 0}
            >
              <Box
                component="img"
                src={AssignIcon}
                alt="Assign"
                sx={masterTemplateCardStyles.assignIcon(theme)}
              />
              Assign
            </Link>
          )}
        </Box>
      )}
      {imageUrl && (
        <Box sx={masterTemplateCardStyles.imageContainer(theme)}>
          <Box
            component="img"
            src={imageUrl}
            alt={imageAlt || name}
            sx={masterTemplateCardStyles.image(theme)}
          />
        </Box>
      )}
      <Box sx={masterTemplateCardStyles.contentContainer(theme)}>
        <Typography sx={masterTemplateCardStyles.title(theme)}>{name}</Typography>
        {description && (
          <Typography sx={masterTemplateCardStyles.description(theme, descriptionMaxLines)}>
            {description}
          </Typography>
        )}
        {shouldMountMetadata && (
          <Box
            role="group"
            aria-label="Template metadata"
            hidden={!hasVisibleMetadata}
            sx={masterTemplateCardStyles.labelsAndStandardsContainer(theme)}
          >
            {masterTypeLabels.length > 0 && (
              <Box sx={masterTemplateCardStyles.labelsContainer(theme)}>
                {masterTypeLabels.map((labelValue, index) => (
                  <Badge
                    key={index}
                    text={labelValue.label}
                    variant={labelValue.id === 1 ? 'success' : 'neutral'}
                  />
                ))}
              </Box>
            )}
            {hasStandardsSource && (
              <Box sx={masterTemplateCardStyles.standardsContainer(theme)}>
                <Standards
                  standardSetId={standardSetId}
                  standardSetLabel={standardSetLabel}
                  templateId={templateId}
                  gradeId={gradeId}
                  standards={inlineStandards}
                  maxStandards={maxStandards}
                  breakouts={!!standards}
                  showBreakouts={true}
                  onExpectationHeightChange={handleStandardsExpectationHeightChange}
                  onStandardsContentChange={handleStandardsContentChange}
                />
              </Box>
            )}
            {hasBadges && (
              <Box sx={masterTemplateCardStyles.badgeContainer(theme)}>
                {showSmallGroups && (
                  <Badge text="Small Groups" />
                )}
                {showReadingGroups && (
                  <Badge text="Reading Groups" />
                )}
                {showTeacherNav && (
                  <Badge text="Teacher Nav" />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </MuiCard>
  );
};

export default MasterTemplateCard;
